import { useCallback, useState } from 'react';
import { ApiException } from '@/types/api';

/**
 * Maps a failed API call onto form state — BUILD-PLAN §41/§42.
 *
 * The backend is the authority on validation (§10 "never trust frontend
 * validation"), so its field errors must reach the right inputs rather than
 * being flattened into one generic message. This hook separates the two:
 * `fieldErrors` bind to inputs, `formError` covers everything else.
 */
export function useFormErrors() {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setFieldErrors({});
    setFormError(null);
  }, []);

  const handleError = useCallback((error: unknown) => {
    if (error instanceof ApiException) {
      const fields = error.fieldErrors();

      setFieldErrors(fields);

      /*
       | Only show a form-level message when there are no field errors to
       | show. Otherwise the customer sees the same complaint twice — once at
       | the top and once beside the input.
       */
      setFormError(Object.keys(fields).length > 0 ? null : error.message);

      return;
    }

    // Never surface a raw exception message to a customer (§41).
    setFormError('Something went wrong. Please try again.');
  }, []);

  return { fieldErrors, formError, handleError, reset, setFormError };
}
